/* Run Traces panel — agent run list + per-run decision detail (Langfuse-styled) */

import { esc, escAttr, setPanelLoading } from "./state.js";
import { api } from "./api.js";


// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Format a duration in seconds to a compact human-readable string. */
export function formatIntervalSeconds(s) {
  if (s == null || s < 0) return "";
  if (s < 1) return `${Math.round(s * 1000)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = String(Math.round(s % 60)).padStart(2, "0");
  return `${m}m ${rem}s`;
}

/**
 * Compact "time ago" for the run list (hosted list rows carry created_at;
 * local JSONL rows don't — callers hide the column on ""/null input).
 */
export function relTime(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Waterfall bar math helpers
// ---------------------------------------------------------------------------

const MIN_BAR_PCT = 1;

/**
 * Offset of a decision's start as a percentage of the run span.
 * Returns 0 when spanMs <= 0 or startMs is null (graceful fallback).
 */
function timelineOffsetPct(startMs, runStartMs, spanMs) {
  if (!(spanMs > 0) || startMs == null) return 0;
  return Math.max(0, ((startMs - runStartMs) / spanMs) * 100);
}

/**
 * Width of a decision bar as a percentage of the run span.
 * Clamped to [MIN_BAR_PCT, 100]. Returns 0 when spanMs <= 0 or durationMs is null.
 */
function timelineWidthPct(durationMs, spanMs) {
  if (!(spanMs > 0) || durationMs == null) return 0;
  return Math.min(100, Math.max((durationMs / spanMs) * 100, MIN_BAR_PCT));
}

/** Map a trace status to a CSS class for the Langfuse-styled badge. */
export function statusClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "success") return "trace-status-success";
  if (s === "error" || s === "failed")      return "trace-status-error";
  return "trace-status-warning";
}

/**
 * Format run-level token usage into a compact badge string.
 * Accepts any of the Anthropic / OpenAI usage dict shapes.
 */
export function formatTokenCounts(usage) {
  if (!usage || typeof usage !== "object") return "";
  const inp = (usage.input_tokens || usage.prompt_tokens || 0);
  const out = (usage.output_tokens || usage.completion_tokens || 0);
  const total = inp + out;
  if (!total) return "";
  const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  return `${fmt(inp)}↑ ${fmt(out)}↓`;
}

// ---------------------------------------------------------------------------
// Container helper
// ---------------------------------------------------------------------------

function _container() {
  return document.getElementById("panel-traces");
}

// ---------------------------------------------------------------------------
// List rendering
// ---------------------------------------------------------------------------

function _truncate(str, maxLen) {
  if (!str) return "";
  return str.length > maxLen ? str.slice(0, maxLen) + "…" : str;
}

function _render(runs) {
  if (!runs.length) {
    return `<div class="trace-empty">No traces yet. Run the agent to see decision traces here.</div>`;
  }

  const rows = runs.map(r => {
    const cls     = statusClass(r.status);
    const prompt  = r.user_prompt || "";
    const truncated = _truncate(prompt, 80);
    const durationS = r.duration_ms != null ? r.duration_ms / 1000 : null;
    const durStr  = durationS != null ? formatIntervalSeconds(durationS) : "";
    const toolCnt = r.tool_count != null ? r.tool_count : 0;
    const toolStr = `${toolCnt} tool${toolCnt !== 1 ? "s" : ""}`;
    const meta    = [durStr, toolStr].filter(Boolean).join(" · ");
    // Time-ago renders only when the payload carries created_at (hosted list
    // rows do; local JSONL records have no wall-clock field).
    const when    = relTime(r.created_at);
    const whenHtml = when ? `<span class="trace-when">${esc(when)}</span>` : "";

    return `<div class="trace-row" data-trace="${escAttr(r.trace_id || "")}">
      <span class="trace-status-badge ${escAttr(cls)}">${esc(r.status || "unknown")}</span>
      <span class="trace-prompt" title="${escAttr(prompt)}">${esc(truncated)}</span>
      ${whenHtml}
      <span class="trace-meta">${esc(meta)}</span>
    </div>`;
  }).join("");
  // .trace-list is an inert wrapper in v1; the v2 uniformity pass styles it
  // as the centered card every other list view uses (traces.css tail).
  return `<div class="trace-list">${rows}</div>`;
}

// ---------------------------------------------------------------------------
// Detail rendering helpers
// ---------------------------------------------------------------------------

function _prettyJson(obj) {
  if (obj == null) return "null";
  try { return JSON.stringify(obj, null, 2); }
  catch { return String(obj); }
}

/** Render a single IO block — reasoning / input / output / error. */
function _ioBlock(label, content, extraCls) {
  return `<div class="td-io-block${extraCls ? " " + escAttr(extraCls) : ""}">
    <div class="td-io-label">${esc(label)}</div>
    ${content}
  </div>`;
}

const _MAX_PAYLOAD = 2000;

function _bodyText(text) {
  if (!text) return `<div class="td-io-body"></div>`;
  const display = text.length > _MAX_PAYLOAD
    ? esc(text.slice(0, _MAX_PAYLOAD)) + `…<span class="td-io-truncated"> (${text.length} chars total)</span>`
    : esc(text);
  return `<div class="td-io-body">${display}</div>`;
}

function _bodyPre(text) {
  const display = text.length > _MAX_PAYLOAD
    ? esc(text.slice(0, _MAX_PAYLOAD)) + `…<span class="td-io-truncated"> (${text.length} chars total)</span>`
    : esc(text);
  return `<pre class="td-io-pre">${display}</pre>`;
}

function _renderDecision(d, runStart, span) {
  const okCls   = d.ok ? "trace-status-success" : "trace-status-error";
  const okLabel = d.ok ? "ok" : "error";
  const preview = _truncate(d.result_preview || "", 60);

  // Per-decision detail: Reasoning FIRST (featured), then Input, Output, Error
  let detail = "";
  if (d.reasoning) {
    detail += _ioBlock("Reasoning", _bodyText(d.reasoning), "td-io-reasoning");
  }
  const argsJson = _prettyJson(d.args);
  detail += _ioBlock("Input", _bodyPre(argsJson), "");
  if (d.result_preview != null) {
    detail += _ioBlock("Output", _bodyText(d.result_preview), "");
  }
  if (d.error) {
    detail += _ioBlock("Error", _bodyText(d.error), "td-io-error");
  }

  // Waterfall bar — only when timing is present and the run span is non-zero.
  let waterfallHtml = "";
  if (d.start_ms != null && d.duration_ms != null && span > 0) {
    const off    = timelineOffsetPct(d.start_ms, runStart, span);
    const w      = timelineWidthPct(d.duration_ms, span);
    const dur    = formatIntervalSeconds(d.duration_ms / 1000);
    const barCls = d.ok ? "ok" : (d.ok === false ? "error" : "");
    waterfallHtml = `<div class="td-waterfall"><div class="td-waterfall-bar ${escAttr(barCls)}" style="left:${off}%;width:${w}%" title="${escAttr(dur)}">${esc(dur)}</div></div>`;
  }

  return `
    <div class="td-decision-row" data-seq="${escAttr(String(d.seq || 0))}">
      <span class="td-tool-badge">TOOL</span>
      <span class="td-decision-name">${esc(d.tool || "")}</span>
      <span class="trace-status-badge ${escAttr(okCls)}">${esc(okLabel)}</span>
      <span class="td-decision-preview">${esc(preview)}</span>
    </div>
    ${waterfallHtml}
    <div class="td-decision-detail" hidden>${detail}</div>`;
}

/** Render the full detail view for a RunRecord. */
function _renderDetail(run) {
  const decisions = (Array.isArray(run.decisions) ? run.decisions : [])
    .slice()
    .sort((a, b) => (a.seq || 0) - (b.seq || 0));

  // Compute the run span from timed decisions (for waterfall bars).
  const timed = decisions.filter(d => d.start_ms != null && d.duration_ms != null);
  let runStart = null, span = 0;
  if (timed.length) {
    runStart = Math.min(...timed.map(d => d.start_ms));
    const runEnd = Math.max(...timed.map(d => d.start_ms + d.duration_ms));
    span = runEnd - runStart;
  }

  const cls      = statusClass(run.status);
  const durationS = run.duration_ms != null ? run.duration_ms / 1000 : null;
  const durStr   = durationS != null ? `Latency: ${formatIntervalSeconds(durationS)}` : "";
  const toolStr  = `${decisions.length} tool${decisions.length !== 1 ? "s" : ""}`;
  const usageStr = formatTokenCounts(run.usage);

  const whenStr  = relTime(run.created_at);

  const chips = [
    durStr   ? `<span class="td-meta-chip">${esc(durStr)}</span>` : "",
    `<span class="td-meta-chip">${esc(toolStr)}</span>`,
    whenStr  ? `<span class="td-meta-chip">${esc(whenStr)}</span>` : "",
    run.autonomy ? `<span class="td-meta-chip">${esc(run.autonomy)}</span>` : "",
    usageStr ? `<span class="td-meta-chip">${esc(usageStr)}</span>` : "",
  ].filter(Boolean).join("");

  const outcomeHtml = run.outcome
    ? `<div class="td-outcome">${esc(run.outcome)}</div>`
    : "";

  const decisionRows = decisions.length
    ? decisions.map(d => _renderDecision(d, runStart, span)).join("")
    : `<div class="trace-empty">No decisions recorded for this run.</div>`;

  return `
    <div class="trace-detail">
      <div class="trace-detail-nav">
        <button class="trace-back-btn" type="button">← Traces</button>
      </div>
      <div class="trace-detail-header">
        <div class="td-prompt">${esc(run.user_prompt || "")}</div>
        <div class="td-badges">
          <span class="trace-status-badge ${escAttr(cls)}">${esc(run.status || "unknown")}</span>
          ${chips}
        </div>
        ${outcomeHtml}
      </div>
      <div class="trace-detail-decisions">${decisionRows}</div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Detail opener
// ---------------------------------------------------------------------------

// Monotonic token: each openTraceDetail call captures the current value; after
// its async fetch resolves it bails if a newer click has superseded it, so a
// slow fetch can never overwrite a fresher detail (or the run list).
let _detailReq = 0;

async function openTraceDetail(traceId) {
  const panel = _container();
  if (!panel) return;

  // Set synchronously so Scenario C / any external observer sees it immediately.
  panel.dataset.activeTrace = traceId;

  const body = panel.querySelector("#tracesBody");
  if (!body) return;

  const token = ++_detailReq;
  body.innerHTML = `<div class="trace-empty">Loading…</div>`;

  try {
    const data = await api.traceDetail(traceId);
    if (token !== _detailReq) return;   // superseded by a newer click
    body.innerHTML = _renderDetail(data);
  } catch (e) {
    if (token !== _detailReq) return;   // superseded — don't clobber newer view
    body.innerHTML = `<div class="trace-empty">Could not load trace: ${esc(String(e.message || e))}</div>`;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Wire the panel's event listeners.
 * Safe to call multiple times (guarded by _tracesWired flag on the element).
 */
export function initTracesPanel() {
  const el = _container();
  if (!el || el._tracesWired) return;
  el._tracesWired = true;
  // Delegate all clicks so the listener survives innerHTML refreshes.
  el.addEventListener("click", _onClick);
  // Refresh button (may be re-created on initTracesPanel calls, so use delegation too).
  document.addEventListener("click", (e) => {
    if (e.target.closest && e.target.closest("#btnRefreshTraces")) refreshTraces();
  }, { capture: false });
}

/** Called whenever the panel becomes the active left-zone panel. */
export function onTracesActivated() {
  refreshTraces();
}

/** Fetch the run list and re-render the panel body. */
export async function refreshTraces() {
  const el = _container();
  if (!el) return;
  const body = el.querySelector("#tracesBody");
  if (!body) return;

  // Supersede any in-flight detail fetch so a slow traceDetail() can't overwrite
  // the run list after the user navigates Back / refreshes.
  _detailReq++;

  setPanelLoading("traces", true);
  try {
    const data = await api.traces();
    const runs = (data && Array.isArray(data.runs)) ? data.runs : [];
    body.innerHTML = _render(runs);
  } catch (e) {
    body.innerHTML = `<div class="trace-empty">Could not load traces: ${esc(String(e.message || e))}</div>`;
  } finally {
    setPanelLoading("traces", false);
  }
}

// ---------------------------------------------------------------------------
// Click handler (delegated — handles list view, detail nav, and accordions)
// ---------------------------------------------------------------------------

function _onClick(ev) {
  // Back button → return to the run list
  if (ev.target.closest(".trace-back-btn")) {
    refreshTraces();
    return;
  }

  // Decision row accordion toggle (inside the detail view).
  // A waterfall bar may be inserted between the row and the detail — skip it.
  const decRow = ev.target.closest(".td-decision-row");
  if (decRow) {
    let next = decRow.nextElementSibling;
    if (next && next.classList.contains("td-waterfall")) next = next.nextElementSibling;
    const detail = next;
    if (detail && detail.classList.contains("td-decision-detail")) {
      detail.hidden = !detail.hidden;
      decRow.classList.toggle("td-decision-open", !detail.hidden);
    }
    return;
  }

  // Run row click → open the detail
  const row = ev.target.closest(".trace-row");
  if (!row) return;
  const traceId = row.dataset.trace;
  if (traceId) openTraceDetail(traceId);
}
